/* eslint-disable react/prop-types */
import { ApiClient } from '@api/api-configuration';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import { captureError } from '@app/lib/Sentry';
import DoctorSelectGeneric from '@feat-doctors/components/DoctorSelect/DoctorSelectGeneric';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomComponentInputs } from '../filters.types';

/**
 * Doctor select wrapper for query filter system
 * Handles both single and multiple selection modes
 * Fetches doctor objects when IDs are provided from URL
 */
export const DoctorFilterSelect: React.FC<CustomComponentInputs> = ({ onChange, value }) => {
  const { t } = useTranslation();
  const [loadedDoctors, setLoadedDoctors] = useState<Doctor[]>([]);

  // Normalize value to always be an array for mode="multiple"
  const normalizedValue = useMemo(() => {
    if (!value) return undefined;
    if (Array.isArray(value)) {
      return value.map((v) => Number(v));
    }
    return [Number(value)];
  }, [value]);

  // Fetch doctors by IDs when value is provided (e.g., from URL)
  useEffect(() => {
    const fetchDoctorsByIds = async (ids: number[]) => {
      try {
        const doctors = await Promise.all(
          ids.map(async (id) => {
            try {
              const response = await ApiClient.doctorsApi.doctors.getDoctorById(id);
              return response.data as Doctor;
            } catch (error) {
              captureError(error instanceof Error ? error : new Error(`Error fetching doctor ID ${id}`), {
                doctorId: id,
                component: 'DoctorFilterSelect',
              });
              return null;
            }
          })
        );
        const filtered = doctors.filter((d) => d !== null) as Doctor[];
        setLoadedDoctors(filtered);
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('Error fetching doctors'), {
          doctorIds: ids,
          component: 'DoctorFilterSelect',
        });
      }
    };

    if (normalizedValue && normalizedValue.length > 0) {
      fetchDoctorsByIds(normalizedValue);
    } else {
      setLoadedDoctors([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValue]);

  const handleChange = (doctor: Doctor | Doctor[] | undefined) => {
    if (Array.isArray(doctor)) {
      // Multiple selection - return array of IDs
      onChange(doctor.map((d) => d.doctorId));
    } else if (doctor) {
      // Single selection - return single ID
      onChange(doctor.doctorId);
    } else {
      // Cleared
      onChange(null);
    }
  };

  return (
    <DoctorSelectGeneric
      allowClear={true}
      mode="multiple"
      placeholder={t('filters-select-doctors-placeholder')}
      maxTagCount="responsive"
      onDoctorChange={handleChange}
      value={normalizedValue}
      selectedItemsProvider={() => (loadedDoctors.length > 0 ? loadedDoctors : undefined)}
      style={{ width: '100%', height: '44px' }}
      className="h-8"
    />
  );
};
